import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation } from "@tanstack/react-query";
import { ChevronRight, ListOrdered, Loader2, MapPin, Printer } from "lucide-react";

import { ZplLocationSelect } from "@/components/ZplLocationSelect";
import { Button } from "@/components/ui/Button";
import { hubModalOverlayProps, hubModalOverlayClassName } from "@/components/ui/HubModalOverlay";
import { useHubToast } from "@/components/ui/HubToast";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

import type { KittingCartSlot } from "./kittingCartModel";
import {
  assignKittingCartMarks,
  canAddKittingCartSlot,
  kittingCartMarkForSlot,
  kittingCartMarkRowClasses,
  MAX_KITTING_CART_MARKS,
  orderedKittingSelectedSlotKeys,
  type KittingCartMark,
} from "./kittingCartMarks";
import {
  sharedStorageLocationsBetweenSlots,
  storageLocationsForCartSlot,
} from "./kittingOrderSelectionModel";
import type { KittingPickLine } from "./kittingPickModel";
import {
  kittingCartLabelPayloadFromSlot,
  printKittingCartLabels,
} from "./kittingLabelApi";
import { useKittingZplPrinter } from "./kittingZplPrinter";

function KittingCartMarkBadge({
  mark,
  size = "md",
}: {
  mark: KittingCartMark;
  size?: "md" | "sm";
}): JSX.Element {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-full font-bold",
        mark.badgeClass,
        size === "sm" ? "h-6 w-6 text-xs" : "h-7 w-7 text-sm",
      )}
      aria-label={`Cart bin ${mark.letter}`}
    >
      {mark.letter}
    </span>
  );
}

function CoLocatedSlotSharedStops({
  anchorSlotId,
  slotId,
  pickLines,
}: {
  anchorSlotId: string;
  slotId: string;
  pickLines: readonly KittingPickLine[];
}) {
  const shared = sharedStorageLocationsBetweenSlots({
    anchorSlotId,
    otherSlotId: slotId,
    pickLines,
  });
  if (shared.length === 0) return null;
  return (
    <p className="mt-1 font-mono text-[11px] text-slate-500">Stops: {shared.join(", ")}</p>
  );
}

export function KittingOrderSelectModal({
  open,
  anchorSlot,
  coLocatedSlots,
  cartSlots,
  pickLines,
  onClose,
  onConfirm,
}: {
  open: boolean;
  anchorSlot: KittingCartSlot | null;
  coLocatedSlots: KittingCartSlot[];
  cartSlots: readonly KittingCartSlot[];
  pickLines: readonly KittingPickLine[];
  onClose: () => void;
  onConfirm: (orderedSlotIds: string[]) => void;
}): JSX.Element | null {
  const { showToast } = useHubToast();
  const { station, setStation, selectedPrinter, unavailableMessage } = useKittingZplPrinter();
  const [selectedSlotIds, setSelectedSlotIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open || anchorSlot == null) return;
    setSelectedSlotIds(new Set([anchorSlot.slotKey]));
  }, [open, anchorSlot?.slotKey]);

  const slotById = useMemo(() => new Map(cartSlots.map((slot) => [slot.slotKey, slot])), [cartSlots]);

  const orderedSelectedSlotKeys = useMemo(() => {
    if (anchorSlot == null) return [];
    return orderedKittingSelectedSlotKeys(anchorSlot.slotKey, selectedSlotIds);
  }, [anchorSlot, selectedSlotIds]);

  const cartMarks = useMemo(
    () => assignKittingCartMarks(orderedSelectedSlotKeys),
    [orderedSelectedSlotKeys],
  );

  const selectedSlots = useMemo(() => {
    const slots: KittingCartSlot[] = [];
    for (const slotId of orderedSelectedSlotKeys) {
      const slot = slotById.get(slotId);
      if (slot != null) slots.push(slot);
    }
    return slots;
  }, [orderedSelectedSlotKeys, slotById]);

  const selectionAtCap = selectedSlotIds.size >= MAX_KITTING_CART_MARKS;

  const anchorLocations = useMemo(() => {
    if (anchorSlot == null) return [];
    return Array.from(storageLocationsForCartSlot(pickLines, anchorSlot.slotKey)).sort((left, right) =>
      left.localeCompare(right, undefined, { sensitivity: "base" }),
    );
  }, [anchorSlot, pickLines]);

  const printLabelsMutation = useMutation({
    mutationFn: (body: { printer: string; labels: ReturnType<typeof kittingCartLabelPayloadFromSlot>[] }) =>
      printKittingCartLabels(body),
    onSuccess: (data) => {
      const count = data.labelCount;
      showToast({
        tone: "success",
        message:
          count === 1
            ? "Cart bin label sent to printer."
            : `${String(count)} cart bin labels sent to printer.`,
      });
    },
    onError: (error: ApiError) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open || anchorSlot == null) return null;

  const anchorMark = kittingCartMarkForSlot(cartMarks, anchorSlot.slotKey);

  const toggleSlot = (slotKey: string, checked: boolean): void => {
    if (slotKey === anchorSlot.slotKey) return;
    if (checked && !canAddKittingCartSlot(selectedSlotIds.size)) {
      showToast({
        tone: "info",
        message: `You can pick up to ${String(MAX_KITTING_CART_MARKS)} jobs per cart session.`,
      });
      return;
    }
    setSelectedSlotIds((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(slotKey);
      } else {
        next.delete(slotKey);
      }
      next.add(anchorSlot.slotKey);
      return next;
    });
  };

  const handlePrintLabels = (): void => {
    if (selectedPrinter.trim() === "") {
      showToast({
        tone: "error",
        message: unavailableMessage ?? "Pick a location.",
      });
      return;
    }
    if (selectedSlots.length === 0) {
      showToast({ tone: "error", message: "Select at least one job to print." });
      return;
    }
    printLabelsMutation.mutate({
      printer: selectedPrinter,
      labels: selectedSlots.map((slot) =>
        kittingCartLabelPayloadFromSlot(slot, kittingCartMarkForSlot(cartMarks, slot.slotKey)),
      ),
    });
  };

  return createPortal(
    <div
      {...hubModalOverlayProps(true)}
      role="dialog"
      aria-modal="true"
      aria-labelledby="kitting-order-select-title"
      className={cn(hubModalOverlayClassName(true), "py-6 sm:py-10")}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[min(88vh,calc(100vh-3rem))] w-full max-w-xl flex-col rounded-lg border border-slate-200 bg-white shadow-xl">
        <div className="shrink-0 border-b border-slate-100 px-4 py-3">
          <h2 id="kitting-order-select-title" className="text-lg font-semibold text-slate-900">
            Start picking
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            Other jobs share storage stops with{" "}
            <span className="font-semibold text-slate-800">{anchorSlot.headlineLabel}</span>. Add them to pick
            together, or continue with this job only.
          </p>
          {anchorLocations.length > 0 ? (
            <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-slate-600">
              <MapPin className="h-3.5 w-3.5 shrink-0 text-slate-500" aria-hidden />
              {anchorLocations.map((location) => (
                <span
                  key={location}
                  className="rounded-md border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-slate-800"
                >
                  {location}
                </span>
              ))}
            </div>
          ) : null}
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-3">
          <section aria-labelledby="kitting-order-anchor-heading" className="space-y-2">
            <h3 id="kitting-order-anchor-heading" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Your job
            </h3>
            <label
              className={cn(
                "flex cursor-default items-start gap-3 rounded-lg border border-l-4 p-3",
                kittingCartMarkRowClasses(anchorMark, true),
              )}
            >
              <input
                type="checkbox"
                className="mt-1"
                checked
                disabled
                aria-label={`${anchorSlot.headlineLabel} (required)`}
              />
              {anchorMark != null ? <KittingCartMarkBadge mark={anchorMark} /> : null}
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-slate-900">{anchorSlot.headlineLabel}</p>
                <p className="text-xs text-slate-600">{anchorSlot.summaryLine}</p>
              </div>
            </label>
          </section>

          {coLocatedSlots.length > 0 ? (
            <section aria-labelledby="kitting-order-colocated-heading" className="space-y-2">
              <h3
                id="kitting-order-colocated-heading"
                className="flex items-center gap-2 text-sm font-semibold text-slate-800"
              >
                <ListOrdered className="h-4 w-4 text-slate-500" aria-hidden />
                Other jobs at same stops
              </h3>
              <ol className="list-none space-y-2 pl-0">
                {coLocatedSlots.map((slot, index) => (
                  <LaterCoLocatedRow
                    key={slot.slotKey}
                    stepNumber={index + 1}
                    anchorSlotId={anchorSlot.slotKey}
                    slot={slot}
                    pickLines={pickLines}
                    checked={selectedSlotIds.has(slot.slotKey)}
                    mark={kittingCartMarkForSlot(cartMarks, slot.slotKey)}
                    selectionAtCap={selectionAtCap}
                    onToggle={(checked) => toggleSlot(slot.slotKey, checked)}
                  />
                ))}
              </ol>
            </section>
          ) : (
            <p className="text-sm text-slate-600">No other active jobs share these storage stops.</p>
          )}

          {selectionAtCap ? (
            <p className="text-xs text-slate-600">
              Up to {String(MAX_KITTING_CART_MARKS)} jobs per cart pick (letters A–G). Uncheck a job to add a
              different one.
            </p>
          ) : null}
        </div>

        <div className="shrink-0 space-y-3 border-t border-slate-100 px-4 py-3">
          <ZplLocationSelect
            id="kitting-order-select-location"
            label="Location"
            value={station}
            onChange={setStation}
            unavailableMessage={unavailableMessage}
            disabled={printLabelsMutation.isPending}
          />
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Button
              type="button"
              variant="secondary"
              className="gap-1.5"
              disabled={printLabelsMutation.isPending || selectedSlots.length === 0}
              onClick={handlePrintLabels}
            >
              {printLabelsMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Printer className="h-4 w-4" aria-hidden />
              )}
              Print labels
              {selectedSlots.length > 1 ? ` (${String(selectedSlots.length)})` : ""}
            </Button>
            <div className="flex gap-2">
              <Button type="button" variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button type="button" onClick={() => onConfirm(orderedSelectedSlotKeys)}>
                Start pick
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function LaterCoLocatedRow({
  stepNumber,
  anchorSlotId,
  slot,
  pickLines,
  checked,
  mark,
  selectionAtCap,
  onToggle,
}: {
  stepNumber: number;
  anchorSlotId: string;
  slot: KittingCartSlot;
  pickLines: readonly KittingPickLine[];
  checked: boolean;
  mark: KittingCartMark | null;
  selectionAtCap: boolean;
  onToggle: (checked: boolean) => void;
}) {
  const checkboxDisabled = !checked && selectionAtCap;

  return (
    <li>
      <label
        className={cn(
          "flex cursor-pointer flex-wrap items-center justify-between gap-3 rounded-lg border border-l-4 px-4 py-3 text-left shadow-sm transition-colors",
          checkboxDisabled ? "cursor-not-allowed opacity-60" : "hover:border-slate-300 hover:bg-slate-50",
          kittingCartMarkRowClasses(mark, checked),
        )}
      >
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-sm font-semibold text-slate-700">
            {stepNumber}
          </span>
          <input
            type="checkbox"
            className="sr-only"
            checked={checked}
            disabled={checkboxDisabled}
            onChange={(event) => onToggle(event.currentTarget.checked)}
            aria-label={`Add ${slot.headlineLabel} to this pick`}
          />
          {checked && mark != null ? <KittingCartMarkBadge mark={mark} size="sm" /> : null}
          <div className="min-w-0 flex-1">
            <p className="font-medium text-slate-900">{slot.headlineLabel}</p>
            <p className="text-xs text-slate-500">{slot.summaryLine}</p>
            <CoLocatedSlotSharedStops anchorSlotId={anchorSlotId} slotId={slot.slotKey} pickLines={pickLines} />
          </div>
        </div>
        <span className="inline-flex items-center gap-1 text-sm font-medium text-brand-700">
          Add
          <ChevronRight className="h-4 w-4" aria-hidden />
        </span>
      </label>
    </li>
  );
}
